import './styles.sass';

import React, { useMemo, useRef, useState } from 'react';
import Transition from 'components/Explorer/Transition';
import { some, sumBy } from 'lodash-es';

import Category from './category';
import LabelCategory from './label_category';
import ChartCategory from './chart_category';
import InspectorCategory from './InspectorCategory';

import useExplorerData, { Loading } from 'hooks/useExplorerData';
import useQueryParams from 'hooks/useQueryParams';

const DEFAULT_BACKGROUND = '#eceff1';

const Breakdown = () => {
  const params = useQueryParams();
  const { data, loading } = useExplorerData('highlights/breakdown', params, { reloadOn: ['filters'] });

  const [inspecting, setInspecting] = useState(null);
  const [mouseOver, setMouseOver] = useState(false);

  const { categories, categoryRefs } = useMemo(() => {
    if (!data) return { categories: [], categoryRefs: [] };
    const built = Category.buildFromData(data);
    return {
      categories: built,
      categoryRefs: built.map((c) => ({ name: c.name, ref: React.createRef() }))
    };
  }, [data]);

  const inspectingRef = useRef(inspecting);
  inspectingRef.current = inspecting;

  const isLoading = loading === Loading.NOT_STARTED || loading === Loading.IN_PROGRESS;
  const hasResults = some(categories, 'mentionCount');

  const inspectCategory = (category, fromMouse = false) => {
    Analytics.trackEvent('highlights-breakdown-inspect-category', { category: category.name });
    setInspecting(category);
    setMouseOver(fromMouse);
  };

  const unInspectCategory = () => {
    setInspecting(null);
    setMouseOver(false);
  };

  const handleCloseInspector = () => {
    const category = inspectingRef.current;
    unInspectCategory();
    categoryRefs.find((cr) => cr.name === category.name).ref.current.focus();
  };

  const inspector = () => {
    const totalMentions = sumBy(categories, 'mentionCount');
    const { name, mentionCount, sources } = inspecting;
    return (
      <div className="Highlights-Breakdown-inspector">
        <InspectorCategory
          categoryName={I18n.t(name, { scope: 'Explorer.Highlights.Breakdown.categories' })}
          categoryMentionCount={mentionCount}
          categorySources={sources}
          totalMentions={totalMentions}
          onCloseInspector={handleCloseInspector}
        />
      </div>
    );
  };

  const chartCategories = () => {
    const totalMentions = sumBy(categories, 'scaledMentionCount');
    return (
      <div className="categories">
        {categories.map(
          (c) =>
            c.mentionCount > 0 && (
              <ChartCategory
                key={c.name}
                category={c}
                totalMentions={totalMentions}
                inspecting={inspecting === c}
                onInspect={() => inspectCategory(c, true)}
              />
            )
        )}
      </div>
    );
  };

  const chart = () => {
    const style = { backgroundColor: inspecting ? inspecting.color : DEFAULT_BACKGROUND };
    return (
      <div className="Highlights-Breakdown-chart">
        <div className={`background ${inspecting ? 'inspecting' : 'not-inspecting'}`} style={style} />
        {inspecting ? inspector() : null}
        {chartCategories()}
      </div>
    );
  };

  const labels = () => (
    <div className="Highlights-Breakdown-labels">
      {categories.map((c) => {
        const ref = categoryRefs.find((cr) => cr.name === c.name).ref;
        return <LabelCategory key={c.name} category={c} onClick={() => inspectCategory(c)} ref={ref} />;
      })}
    </div>
  );

  const content = () => {
    if (loading === Loading.ERROR) {
      return <div className="Highlights-error-message">{I18n.t('Explorer.loading_error')}</div>;
    }
    return (
      <div
        key="content"
        className={`Highlights-content Highlights-Breakdown-content ${mouseOver ? 'hide-focus-outline' : ''}`}
        data-testid="content"
        onMouseLeave={unInspectCategory}
      >
        {hasResults ? (
          <div>
            {chart()}
            {labels()}
          </div>
        ) : (
          <div className="Highlights-content-empty-message">{I18n.t('Explorer.Highlights.Breakdown.empty')}</div>
        )}
      </div>
    );
  };

  return (
    <div className="Highlights-Breakdown Highlights-block">
      <h2 className="Highlights-title">{I18n.t('Explorer.Highlights.Breakdown.title')}</h2>
      <div className="Highlights-content-wrapper">
        <Transition>
          {isLoading ? <div key="loadingMessage" className="Highlights-loading-message" /> : content()}
        </Transition>
      </div>
    </div>
  );
};

export default Breakdown;
